import React from 'react';

/**
 * ComplianceFooter — slim, unobtrusive bar of policy links.
 *
 * Compliance requirement: Privacy Policy, Terms & Conditions, and Data
 * Protection Policy MUST be reachable from every page where users transact
 * or share personal data. This component renders that link group consistently
 * across public + authenticated surfaces.
 *
 * Usage:
 *   <ComplianceFooter />          — default centered, on white/light backgrounds
 *   <ComplianceFooter variant="dark" />  — for navy/dark backgrounds
 *   <ComplianceFooter compact />   — for dashboards (smaller text)
 */
export default function ComplianceFooter({ variant = 'light', compact = false, className = '' }) {
  const isDark = variant === 'dark';
  const baseColor = isDark ? 'text-white/60' : 'text-navy/60';
  const linkColor = isDark ? 'hover:text-gold' : 'hover:text-orange';
  const size = compact ? 'text-[11px]' : 'text-xs';
  const sep = isDark ? 'text-white/30' : 'text-navy/30';

  return (
    <div
      className={`w-full ${baseColor} ${size} flex flex-wrap items-center justify-center gap-x-3 gap-y-1 py-3 px-4 ${className}`}
      data-testid="compliance-footer"
      role="contentinfo"
      aria-label="Compliance and legal links"
    >
      <a href="/privacy-policy" target="_blank" rel="noreferrer" className={`underline-offset-2 hover:underline ${linkColor}`} data-testid="compliance-privacy-link">
        Privacy Policy
      </a>
      <span className={sep}>·</span>
      <a href="/terms-and-conditions" target="_blank" rel="noreferrer" className={`underline-offset-2 hover:underline ${linkColor}`} data-testid="compliance-terms-link">
        Terms &amp; Conditions
      </a>
      <span className={sep}>·</span>
      <a href="/data-protection-policy" target="_blank" rel="noreferrer" className={`underline-offset-2 hover:underline ${linkColor}`} data-testid="compliance-dpdp-link">
        Data Protection Policy
      </a>
      <span className={sep}>·</span>
      <span className={baseColor} data-testid="compliance-gstin">GSTIN: 27EYMPS1491H1ZT</span>
    </div>
  );
}
